/* @flow */
import React, { useState, useEffect } from "react";
import { withRouter } from "react-router-dom";
import styled, { css } from "styled-components";
import idx from "idx";
import withData from "./withData";
import { withGettext, inferLocale } from "./common/Gettext";
import connect from "react-redux/es/connect/connect";
import pick from "lodash/fp/pick";
import { Link } from "./common/Link";
import { fontSize, rtl } from "./common/styled-mixins";
import Gettext from "./common/Gettext";
import helpImage from "../images/chat-help.svg";
import FeatureFlags from "../FeatureFlags";
import { isIronTerminal } from "./utils";

const Container = styled.div`
  direction: ${({ theme }) => theme.direction};
  margin-top: 0;
  margin-right: 0;
  margin-bottom: 0;
  padding: 0;
  border: 0;
  overflow: hidden;
  position: fixed;
  z-index: 16000001;
  right: 10px;
  bottom: 0;
  border-top-left-radius: 5px;
  border-top-right-radius: 5px;
  display: block;
  width: 290px;
  height: 400px;
  box-shadow: rgba(0, 0, 0, 0.1) 0 0 3px 2px;

  ${props =>
    props.isMobile &&
    css`
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      min-width: 100%;
      max-width: 100%;
      min-height: 100%;
      max-height: 100%;
      border-radius: 0;
    `};
`;

const Content = styled.div`
  width: 290px;
  height: 400px;
  padding-top: 24px;
  font-size: ${fontSize(12)};
  color: #666;

  ${props =>
    props.isMobile &&
    css`
      width: 100%;
      height: 100%;
      padding-top: 44px;
      font-size: ${fontSize(14)};
      background-color: ${({ theme }) => theme.foreground};
    `};
`;

const ContentWrapper = styled.div`
  height: 376px;
  padding: 20px 20px 0;
  background-color: ${({ theme }) => theme.foreground};

  ${props =>
    props.isMobile &&
    css`
      width: 100%;
      max-width: 360px;
      height: auto;
      margin-left: auto;
      margin-right: auto;
      padding: 10px 10px 0;
    `};
`;

const Header = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  overflow: hidden;
  z-index: 10;
  height: 24px;
  color: #e0edd3;
  background: #6cae1e;
  border-top-left-radius: 5px;
  border-top-right-radius: 5px;
  user-select: none;

  ${props =>
    props.isMobile &&
    css`
      height: 44px;
      border-radius: 0;
    `};
`;

const HeaderText = styled.div`
  position: relative;
  padding-left: 25px;
  padding-right: 25px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  cursor: inherit;
  text-align: center;
  font-size: ${fontSize(11)};
  line-height: 24px;
  font-weight: bold;

  ${props =>
    props.isMobile &&
    css`
      font-size: ${fontSize(12)};
      line-height: 44px;
    `};
`;

const HeaderClose = styled.div`
  position: absolute;
  top: 0;
  right: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 25px;
  height: 24px;
  padding-top: 2px;
  padding-right: 3px;
  cursor: pointer;

  ${props =>
    props.isMobile &&
    css`
      width: 44px;
      height: 44px;
      padding: 0;
    `};
`;

const CloseLine = styled.div`
  width: 10px;
  height: 2px;
  margin: auto;
  background-color: #e0edd3;

  ${props =>
    props.isMobile &&
    css`
      width: 16px;
    `};
`;

const HelpButtonContainer = styled.div`
  position: absolute;
  bottom: 24px;
  left: 0;
  width: 100%;
  text-align: center;
  padding: 10px 20px 0;
  height: 40px;
  border: 0 solid #eee;
  border-top-width: 1px;

  ${props =>
    props.isMobile &&
    css`
      position: static;
      padding: 0;
      padding-top: 20px;
      border: none;
    `};
`;

const HelpButton = styled(Link)`
  display: block;
  text-decoration: none;
  -webkit-appearance: none;
  width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  color: #ffffff;
  background: #6cae1e;
  font-size: ${fontSize(12)};
  padding: 8px 3px;
  border: 1px solid #5ca101;
  border-radius: 3px;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.1),
    inset 0 1px 0 rgba(255, 255, 255, 0.25);
  font-weight: bold;
  word-wrap: normal;
  cursor: pointer;

  ${props =>
    props.isMobile &&
    css`
      padding: 10px 3px;
      font-size: ${({ theme }) =>
        theme.locale === "ar" ? theme.minFontSize : "14px"};
    `};

  :hover {
    background: #5ca101;
  }
`;

const Text = styled.p`
  font-size: ${fontSize(14)};
  line-height: 1.2;
  margin-bottom: 10px;
`;

const UniversalContainer = styled.div`
  position: relative;
  display: inline-block;
  padding-top: 30px;
  ${rtl("padding-right", "29px")};
`;

const UniversalImage = styled.img`
  position: absolute;
  top: 0;
  ${rtl("right", 0)};
  display: block;
  width: 36px;
  height: 36px;
`;

const UniversalButton = styled.div`
  font-size: 16px;
  font-weight: bold;
  color: #0085eb;
  cursor: pointer;
  ${rtl("text-align", "left")};
  text-decoration: underline;
  user-select: none;

  :hover {
    text-decoration: none;
  }
`;

const useUserDepartment = (departments, lang, currency) => {
  let department = "";
  // для юзеров с валютой назначем департамент по валюте
  if (currency) {
    switch (currency) {
      case "VND":
        department = "VN";
        break;
      case "PHP":
        department = "PH";
        break;
      case "IDR":
        department = "ID";
        break;
      case "INR":
        department = "HI";
        break;
      case "THB":
        department = "TH";
        break;
      case "UGX":
      case "KES":
      case "GHS":
      case "NGN":
      case "XAF":
      case "ZAR":
      case "TZS":
        department = "AF";
        break;
      default:
        // назначаем департамент по языку юзера, если ниодна валюта не подошла
        if (
          departments.name &&
          departments.name.some(l => l.toLowerCase() === lang)
        ) {
          department = lang.toUpperCase();
        } else {
          department = "EN";
          break;
        }
    }
  } else {
    // здесь определяем департамент для незалогиненных юзеров
    if (
      // назначаем департамент по соответсвующему языку юзера, кроме английского языка
      departments
        .filter(d => d.name.toLowerCase() !== "en")
        .some(l => l.name.toLowerCase() === lang)
    ) {
      department = lang.toUpperCase();
    } else {
      department = "Other";
    }
  }

  return departments.find(dept => dept.name === department);
};

export const animateChatButton = () => {
  const colors = [
    "#60A41B",
    "#8e53c6",
    "#9a5ad7",
    "#8e53c6",
    "#60A41B",
    "#8e53c6",
    "#9a5ad7",
    "#8e53c6",
    "#60A41B"
  ];

  const run = i => {
    if (colors.length > i) {
      window.$zopim.livechat.theme.setColor(colors[i]);
      window.$zopim.livechat.theme.reload();
      setTimeout(() => run(i + 1), 150);
    }
  };

  if (window.$zopim) {
    run(0);
  }
};

const OnlineButton = ({ showButton, onClick }) => {
  if (!showButton) return null;

  return (
    <div style={{ textAlign: "center" }}>
      <UniversalContainer>
        <UniversalButton onClick={onClick}>
          <div>
            <Gettext>Need help?</Gettext> <Gettext>Chat with us</Gettext>
          </div>
        </UniversalButton>
        <UniversalImage src={helpImage} />
      </UniversalContainer>
    </div>
  );
};

const OfflineButton = props => (
  <div style={{ textAlign: "center" }}>
    <UniversalContainer>
      <UniversalButton onClick={props.showForm}>
        <div>
          <Gettext>Need help?</Gettext> <Gettext>Chat with us</Gettext>
        </div>
      </UniversalButton>
      <UniversalImage src={helpImage} />
    </UniversalContainer>
  </div>
);

const OfflineForm = props => {
  const isAuth = props.profile != null;
  const isReal = isAuth && props.profile.real === 1;
  return (
    <Container isMobile={props.isMobile}>
      <Header isMobile={props.isMobile}>
        <HeaderText isMobile={props.isMobile}>
          <Gettext>Offline</Gettext>
        </HeaderText>
        <HeaderClose onClick={props.hideForm} isMobile={props.isMobile}>
          <CloseLine isMobile={props.isMobile} />
        </HeaderClose>
      </Header>
      <Content isMobile={props.isMobile}>
        <ContentWrapper isMobile={props.isMobile}>
          <Text>
            <Gettext>All our operators are offline at this moment.</Gettext>
          </Text>
          <Text>
            {/* prettier-ignore */}
            {/*<Gettext>
              Please get back on weekdays from {workingHours.from} to {workingHours.to} ({3} time).
            </Gettext>*/}
          </Text>
          <Text>
            {isAuth && !isReal ? (
              <Gettext>
                In the meantime, here is the link to our HelpCenter that you
                might find helpful.
              </Gettext>
            ) : (
              <Gettext>
                You can email us and we'll answer as soon as possible
              </Gettext>
            )}
          </Text>
          <HelpButtonContainer isMobile={props.isMobile}>
            {isAuth && !isReal ? (
              <HelpButton
                to="/helpdesk/faq"
                target="_blank"
                onClick={props.goToFaq}
                isMobile={props.isMobile}
              >
                <Gettext>FAQ</Gettext>
              </HelpButton>
            ) : (
              <HelpButton
                to="/helpdesk/contacts"
                target="_blank"
                onClick={props.goToFaq}
                isMobile={props.isMobile}
              >
                <Gettext>Send email</Gettext>
              </HelpButton>
            )}
          </HelpButtonContainer>
        </ContentWrapper>
      </Content>
    </Container>
  );
};

class Chat extends React.PureComponent {
  state = {
    isChatting: false,
    showOnlineButton: false,
    isChatOpening: false,
    showOfflineButton: false,
    showOfflineForm: false
  };

  showOfflineForm = () => {
    this.setState({ showOfflineForm: true });
    if (this.props.isMobile && document.body) {
      document.body.style.cssText =
        "position: fixed;\n" +
        "overflow-y: hidden;\n" +
        "width: 100%;\n" +
        "min-height: 100%;\n" +
        "margin: 0px;";
    }
  };

  hideOfflineForm = () => {
    this.setState({ showOfflineForm: false });
    if (this.props.isMobile && document.body) {
      document.body.style.position = "";
      document.body.style.overflowY = "";
      document.body.style.width = "";
      document.body.style.minHeight = "";
      document.body.style.margin = "";
    }
  };

  goToFaq = () => {
    this.hideOfflineForm();
  };

  showOnlineForm = () => {
    window.$zopim.livechat.window.show();
    this.setState({ showOnlineButton: false, isChatOpening: true });
  };

  setDepartment = (lang, prevLang) => {
    if (!window.$zopim) return null;
    if (typeof prevLang !== "undefined") {
      window.$zopim.livechat.departments.clearVisitorDepartment();
    }

    if (this.props.userDepartment) {
      window.$zopim.livechat.departments.setVisitorDepartment(
        this.props.userDepartment.name
      );
    }
  };

  mounted = false;

  componentDidMount() {
    this.mounted = true;
    const phone = this.props.profile && this.props.profile.phone;
    const id = this.props.profile && this.props.profile.id;
    const email = this.props.profile && this.props.profile.email;
    const first_name = this.props.profile && this.props.profile.first_name;
    const last_name = this.props.profile && this.props.profile.last_name;
    const $zopim = window.$zopim;

    if (typeof $zopim !== "function") return null;

    window.zE(() => {
      $zopim(() => {
        $zopim.livechat.setOnChatStart(() => {
          if (!this.mounted) {
            return;
          }

          if (id) {
            try {
              window
                .fetch(`${window.API_URL}/api/user-support-log`, {
                  method: "POST",
                  body: JSON.stringify({
                    id: Date.now(),
                    user_id: id,
                    channel: "chat",
                    answered: 1
                  })
                })
                .catch(console.log);
            } catch (e) {
              console.error(e);
            }
          }
          this.setState({
            isChatting: true
          });
        });
        $zopim.livechat.setOnChatEnd(() => {
          if (!this.mounted) {
            return;
          }

          this.setState({
            isChatting: false
          });
        });

        $zopim.livechat.window.onHide(() => {
          if (!this.mounted) {
            return;
          }

          $zopim.livechat.hideAll();

          this.setState({
            showOnlineButton: true,
            isChatOpening: false
          });

          if (typeof this.props.onHide === "function") {
            this.props.onHide();
          }
        });
        $zopim.livechat.theme.setColor("#60A41B");
        $zopim.livechat.theme.reload(); // apply new theme settings

        phone != null && $zopim.livechat.setPhone(phone);
        email != null && $zopim.livechat.setEmail(email);
        if (first_name != null && last_name != null) {
          id != null
            ? $zopim.livechat.setName(
                `${first_name} ${last_name} / ${String(id)}`
              )
            : $zopim.livechat.setName(`${first_name} ${last_name}`);
        } else if (first_name != null) {
          id != null
            ? $zopim.livechat.setName(`${first_name} / ${String(id)}`)
            : $zopim.livechat.setName(`${first_name}`);
        }
        if (id != null && first_name == null && last_name == null) {
          $zopim.livechat.setName(`/ ${String(id)}`);
        }
      });
    });
  }

  componentDidUpdate(prevProps, prevState, snapshot) {
    if (window.$zopim && window.$zopim.livechat) {
      if (this.props.profile != null) {
        window.$zopim.livechat.removeTags("unauthorized");
      }

      if (prevProps.lang !== this.props.lang) {
        this.setDepartment(this.props.lang, prevProps.lang);

        window.$zopim.livechat.removeTags(prevProps.lang);
        window.$zopim.livechat.addTags(this.props.lang);
      }
    }
  }

  componentWillUnmount() {
    this.mounted = false;
    const $zopim = window.$zopim;
    if (typeof $zopim !== "function") return null;

    window.zE(() => {
      $zopim(function() {
        $zopim.livechat.hideAll();
      });
    });
  }

  render() {
    const { lang, showWindow } = this.props;
    const isAvailable =
      this.props.isAvailableBySchedule || this.state.isChatting;

    const $zopim = window.$zopim;

    if ($zopim != null) {
      window.zE(() => {
        $zopim(() => {
          // устанавливаем язык чата
          const supportedLangs = ["en", "vn", "th", "id", "hi", "ar"];

          if (supportedLangs.some(l => l === lang)) {
            lang === "vn"
              ? $zopim.livechat.setLanguage("vi")
              : $zopim.livechat.setLanguage(lang);
          } else {
            $zopim.livechat.setLanguage("en");
          }

          // устанавливаем текста для элементов интерфейса чата
          $zopim.livechat.setGreetings({
            online: this.props.gettext`Chat support`,
            offline: this.props.gettext`Chat support`,
            away: this.props.gettext`Chat support`
          });
          $zopim.livechat.window.setTitle(this.props.gettext`Chat support`);
          $zopim.livechat.concierge.setTitle(this.props.gettext`Chat with us`);
          if (process.env.REACT_APP_DEPLOY_TARGET === "irontrade") {
            $zopim.livechat.concierge.setName(
              this.props.gettext(["IronTrade chat support"])
            );
          }

          // устанавливаем тэги и департаменты
          this.setDepartment(lang, undefined);

          $zopim.livechat.addTags(lang);

          if (this.props.profile == null) {
            $zopim.livechat.addTags("unauthorized");
          }

          if (!isAvailable) {
            this.setState({ showOnlineButton: false });
            $zopim.livechat.hideAll();
          }

          if (isAvailable && !this.state.isChatOpening) {
            this.setState({
              showOnlineButton: true
            });
          }

          if (showWindow && isAvailable) {
            $zopim.livechat.window.show();
            this.setState({ showOnlineButton: false, isChatOpening: true });
          }
        });
      });
    }

    return isAvailable ? (
      <OnlineButton
        showButton={this.state.showOnlineButton}
        onClick={this.showOnlineForm}
        buttonPosition={this.props.buttonPosition}
      />
    ) : (
      <React.Fragment>
        <OfflineButton
          isMobile={this.props.isMobile}
          isHelpdesk={this.props.isHelpdesk}
          showForm={this.showOfflineForm}
        />
        {this.state.showOfflineForm && (
          <OfflineForm
            isActive={this.state.showOfflineForm}
            hideForm={this.hideOfflineForm}
            isMobile={this.props.isMobile}
            goToFaq={this.goToFaq}
            lang={lang}
            profile={this.props.profile}
          />
        )}
      </React.Fragment>
    );
  }
}

export const ChatSchedule = props => {
  const [departments, setDepartments] = useState(() => {
    return window.zE("webWidget:get", "chat:departments") || [];
  });

  useEffect(() => {
    let active = true;

    // коллбэк после подключения к чату
    window.zE("webWidget:on", "chat:connected", () => {
      if (!active) {
        return;
      }
      // получаем список департаментов при подключении виджета
      setDepartments(window.zE("webWidget:get", "chat:departments"));
    });

    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    if (departments.length > 0) {
      let active = true;
      window.zE("webWidget:on", "chat:departmentStatus", dept => {
        if (!active) {
          return;
        }
        setDepartments(prevState => {
          let nextState = [];
          let updated = false;

          for (let d of prevState) {
            if (d.id === dept.id) {
              nextState.push(dept);
              updated = true;
            } else {
              nextState.push(d);
            }
          }

          if (!updated) {
            nextState.push(dept);
          }

          return nextState;
        });
      });

      return () => {
        active = false;
      };
    }
  }, [departments.length > 0]);

  const userDepartment = useUserDepartment(
    departments,
    props.lang,
    idx(props, _ => _.profile.currency)
  );

  if (departments.length < 1) {
    return null;
  }

  const isAvailableBySchedule =
    userDepartment != null && userDepartment.status === "online";
  return props.children({
    isAvailableBySchedule,
    userDepartment
  });
};

export const ScheduledChat = props => (
  <ChatSchedule profile={props.profile} lang={props.lang}>
    {({ isAvailableBySchedule, userDepartment }) => (
      <React.Fragment>
        <Chat
          profile={props.profile}
          isMobile={props.isMobile}
          lang={props.lang}
          isHelpdesk={props.isHelpdesk}
          gettext={props.gettext}
          showWindow={props.showWindow}
          isAvailableBySchedule={isAvailableBySchedule}
          userDepartment={userDepartment}
          onHide={props.onHide}
          buttonPosition={props.buttonPosition}
        />
      </React.Fragment>
    )}
  </ChatSchedule>
);

const Routes = props => {
  if (
    props.serverData &&
    !props.serverData.isIOS &&
    process.env.REACT_APP_DEPLOY_TARGET === "irontrade" &&
    FeatureFlags.enableChat &&
    !isIronTerminal()
  ) {
    return (
      <ScheduledChat
        profile={props.profile}
        isMobile={props.serverData.isMobile}
        lang={inferLocale(props)}
        gettext={props.gettext}
        buttonPosition={props.buttonPosition}
      />
    );
  } else {
    return null;
  }
};
export default withRouter(
  withGettext(
    withData({ optional: ["profile", "serverData"] })(
      connect(pick(["fallbackLocale"]))(Routes)
    )
  )
);



// WEBPACK FOOTER //
// ./src/components/Chat.js